import type { DiffBlock, DiffKind } from "./rules-diff";
import { TABLE_ROW, wrapDrop, wrapMark } from "./rules-marks";

/** Cells of a table row, without the leading and trailing pipe. */
const cells = (line: string) =>
  line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|");

const isRule = (line: string) => /^[\s|:-]+$/.test(line);

/** Every cell of one row carrying the same mark. */
const oneSided = (line: string, wrap: (text: string) => string) =>
  `|${cells(line)
    .map((cell) => ` ${wrap(cell.trim())} `)
    .join("|")}|`;

/** True when the two sides of this table do not have the same columns. */
const columnsChanged = (span: { line: string; block: DiffBlock }[]) => {
  const width = (kind: DiffKind) => {
    const row = span.find(
      (entry) =>
        entry.block.kind === kind && !isRule(entry.line) && entry.line.trim(),
    );
    return row ? cells(row.line).length : null;
  };
  const before = width("removed") ?? width("same");
  const after = width("added") ?? width("same");
  return before !== null && after !== null && before !== after;
};

/** One side of a table that changed shape, kept whole. */
const pushSide = (
  out: DiffBlock[],
  span: { line: string; block: DiffBlock }[],
  side: "removed" | "added",
  wrap: (text: string) => string,
) => {
  const rows = span
    .filter((entry) => entry.block.kind === "same" || entry.block.kind === side)
    .map((entry) =>
      entry.block.kind === "same" || isRule(entry.line)
        ? entry.line
        : oneSided(entry.line, wrap),
    );
  if (!rows.some((row) => !isRule(row))) return;
  const owner = span.find((entry) => entry.block.kind !== "same") ?? span[0];
  out.push({
    kind: side,
    lines: rows,
    change: owner.block.change,
    section: owner.block.section,
  });
};

/** One row showing what the cell was and what it became. */
const combineRow = (before: string, after: string) => {
  const was = cells(before);
  const now = cells(after);
  if (was.length !== now.length) return after;
  const merged = now.map((cell, index) => {
    if (cell.trim() === was[index].trim()) return cell;
    return ` ${wrapDrop(was[index].trim())} ${wrapMark(cell.trim())} `;
  });
  return `|${merged.join("|")}|`;
};

/**
 * Keeps a markdown table whole when something inside it changes.
 *
 * Blocks are runs of lines, and a table's rows are lines, so a changed row used
 * to be lifted out of its table into its own block. Rendered alone it had no
 * header to belong to, so it came out as literal pipes — and the rows left
 * behind lost their changed sibling and ran together into one paragraph. The
 * table is reassembled into a single block, and the row that moved carries both
 * values inside it.
 */
export const mergeTables = (blocks: DiffBlock[]): DiffBlock[] => {
  const flat = blocks.flatMap((block) =>
    block.lines.map((line) => ({ line, block })),
  );
  const out: DiffBlock[] = [];
  const push = (block: DiffBlock, line: string) => {
    const last = out[out.length - 1];
    // Section too, the way the first pass splits them. Rebuilding on kind and
    // change alone put a run back together across the heading that had just
    // divided it.
    if (
      last &&
      last.kind === block.kind &&
      last.change === block.change &&
      last.section === block.section
    )
      last.lines.push(line);
    else out.push({ ...block, lines: [line] });
  };

  for (let i = 0; i < flat.length; i += 1) {
    if (!TABLE_ROW.test(flat[i].line)) {
      push(flat[i].block, flat[i].line);
      continue;
    }
    // Take the whole table, however its rows were classified.
    let end = i;
    while (end < flat.length && TABLE_ROW.test(flat[end].line)) end += 1;
    const span = flat.slice(i, end);
    i = end - 1;

    const moved = span.find((entry) => entry.block.kind !== "same");
    if (!moved) {
      span.forEach((entry) => push(entry.block, entry.line));
      continue;
    }

    // A table whose shape changed is two tables, not one.
    //
    // Combining rows cell by cell only means something while the columns line
    // up. When they do not, `combineRow` used to return the new row and drop
    // the old one, so adding a column silently deleted every old value in the
    // table. Both tables are kept instead, one removed and one added.
    if (columnsChanged(span)) {
      pushSide(out, span, "removed", wrapDrop);
      pushSide(out, span, "added", wrapMark);
      continue;
    }

    const rows: string[] = [];
    for (let n = 0; n < span.length; n += 1) {
      const { line, block } = span[n];
      if (block.kind === "same" || isRule(line)) {
        rows.push(line);
        continue;
      }
      if (block.kind === "added") {
        rows.push(oneSided(line, wrapMark));
        continue;
      }
      // Pair the whole run of removed rows with the whole run of added rows
      // that follows it, by position within the two runs. Pairing each removed
      // row with the row directly after it put the first of two edited rows
      // against the second one's replacement, so both rows showed the wrong
      // before-and-after.
      const gone: string[] = [];
      while (
        n < span.length &&
        span[n].block.kind === "removed" &&
        !isRule(span[n].line)
      ) {
        gone.push(span[n].line);
        n += 1;
      }
      const arrived: string[] = [];
      let m = n;
      while (
        m < span.length &&
        span[m].block.kind === "added" &&
        !isRule(span[m].line)
      ) {
        arrived.push(span[m].line);
        m += 1;
      }
      gone.forEach((row, index) => {
        const replacement = arrived[index];
        rows.push(
          replacement ? combineRow(row, replacement) : oneSided(row, wrapDrop),
        );
      });
      arrived
        .slice(gone.length)
        .forEach((row) => rows.push(oneSided(row, wrapMark)));
      n = m - 1;
    }
    out.push({
      kind: "changed",
      lines: rows,
      change: moved.block.change,
      section: moved.block.section,
    });
  }
  return out;
};
